import { subscribe } from 'valtio';
import { putOrgLayerConfig } from '~/api/orgLayerConfig';
import { createDocumentSyncController, type DocumentSyncController } from './documentSyncController';
import orgLayerConfigStore, { type OrgLayerConfigStore } from './orgLayerConfig';
import { stateToDocument } from './orgLayerConfig.mapping';

// ---------------------------------------------------------------------------
// Thin adapter over the generic `createDocumentSyncController` write machine. Simpler than
// `layerConfigSync.ts` (the per-user controller): the document is membership-only,
// there's no `layerStore.pausePersistence` guard, and there are no per-set
// (`visibleLeafIds`/`pinnedLeafIds`) subscriptions — a single deep Valtio subscription on the
// store root catches every edit: every mutator pushes/reassigns the proxied `meta.nodeIds` and
// writes the `libraryLayerIds` proxySet via `rebuildLibraryLayerIds`, so a root subscribe always
// fires. (The plain `missingSince` Map isn't observed by valtio, but it never changes alone.)
// See `documentSyncController.ts` for the write-machine design (baseline diff, single-flight,
// retain-dirty-on-failure).
// ---------------------------------------------------------------------------

/** Trailing debounce window: a burst of edits coalesces into one PUT. */
const DEBOUNCE_MS = 1000;
/** A long continuous burst still flushes at least this often. */
const MAX_WAIT_MS = 5000;

interface OrgLayerConfigSyncTransport {
  put: typeof putOrgLayerConfig;
}

export class OrgLayerConfigSyncController {
  private readonly sync: DocumentSyncController;

  constructor(
    private config: OrgLayerConfigStore = orgLayerConfigStore,
    private transport: OrgLayerConfigSyncTransport = { put: putOrgLayerConfig },
    debounceMs: number = DEBOUNCE_MS,
    maxWaitMs: number = MAX_WAIT_MS,
  ) {
    this.sync = createDocumentSyncController({
      canPersist: () => this.config.canPersist,
      getState: () => this.config.getState(),
      toDocument: stateToDocument,
      put: (organizationId, document) => this.transport.put(organizationId, document),
      subscribe: (onChange) => subscribe(this.config, onChange, true),
      onHydrate: (fn) => this.config.onHydrate(fn),
      onPersistSuccess: () => {
        this.config.saveError = false;
      },
      onPersistError: () => {
        this.config.saveError = true;
      },
      debounceMs,
      maxWaitMs,
    });
  }

  /**
   * Delegate to the generic controller: activate the constructor-wired root Valtio subscription
   * and the store's hydrate lifecycle slot. Idempotent — the page may remount.
   */
  start() {
    this.sync.start();
  }

  /** Tear down the subscription and cancel any pending flush. */
  stop() {
    this.sync.stop();
  }

  /** Whether the in-memory state differs from the last-persisted document. */
  isDirty(): boolean {
    return this.sync.isDirty();
  }

  /** Arm/extend the trailing debounce. No-op while persistence is not armed (`canPersist` false). */
  scheduleFlush() {
    this.sync.scheduleFlush();
  }

  /**
   * The debounced write: serialize current state at fire time, PUT the whole document, and on
   * success move the baseline to what was sent — then re-diff and reschedule if edits arrived
   * during the flight. On failure the baseline is retained and `saveError` is set; the next
   * flush trigger retries. Only one PUT is ever in flight, so writes reach the server in
   * dispatch order and the server's last-write-wins upsert needs no ordering token.
   */
  async flush(): Promise<void> {
    return this.sync.flush();
  }

  /**
   * Immediate flush for lifecycle boundaries: cancel the pending debounce and write now. If a
   * PUT is already in flight, defer to its completion path (which runs one more flush) rather
   * than dropping the request.
   */
  flushNow() {
    this.sync.flushNow();
  }
}

const orgLayerConfigSync = new OrgLayerConfigSyncController();

export default orgLayerConfigSync;
